/**
 * Settings Screen for zWarehouse
 * 
 * LOCAL STORAGE: Printer management now works with local storage and bridge integration.
 * - Edit printer name/location and save locally (persists in browser)
 * - Bridge connection on port 3001 for printer discovery and status
 * - Each user manages their own printer names and locations
 * - IP address is the key identifier for printers
 * - Local customizations are merged with bridge data
 */

import {
    Card,
    CardContent,
    FormControl,
    Grid,
    Input,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    TextField,
    IconButton,
    Alert,
    AlertTitle
} from "@mui/material"
import {  Container } from "@mui/system"
import { useEffect, useState, useCallback } from "react"
import {
    useNotify
} from "react-admin"
import SettingsInputComponentTwoToneIcon from '@mui/icons-material/SettingsInputComponentTwoTone';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import restProvider from '../../rest.provider';

// Local printer storage service
const LocalPrinterStorage = {
    STORAGE_KEY: 'zWarehouse_local_printers',
    
    // Get all locally stored printers
    getLocalPrinters() {
        try {
            const stored = localStorage.getItem(this.STORAGE_KEY);
            return stored ? JSON.parse(stored) : {};
        } catch (error) {
            console.error('Error loading local printers:', error);
            return {};
        }
    },
    
    // Save a printer locally
    savePrinter(printer) {
        try {
            const localPrinters = this.getLocalPrinters();
            localPrinters[printer.ip] = {
                ...printer,
                lastUpdated: new Date().toISOString()
            };
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(localPrinters));
            return true;
        } catch (error) {
            console.error('Error saving printer locally:', error);
            return false;
        }
    },
    
    // Update a printer locally
    updatePrinter(ip, updates) {
        try {
            console.log('LocalPrinterStorage.updatePrinter called with:', { ip, updates });
            const localPrinters = this.getLocalPrinters();
            console.log('Current local printers:', localPrinters);
            
            if (localPrinters[ip]) {
                // Update existing printer
                console.log('Updating existing printer:', localPrinters[ip]);
                localPrinters[ip] = {
                    ...localPrinters[ip],
                    ...updates,
                    lastUpdated: new Date().toISOString()
                };
            } else {
                // Create new printer entry if it doesn't exist
                console.log('Creating new printer entry for IP:', ip);
                localPrinters[ip] = {
                    ip: ip,
                    port: 9100,
                    status: 'unknown',
                    model: null,
                    discoveredAt: new Date().toISOString(),
                    discoveryMethod: 'manual',
                    ...updates,
                    lastUpdated: new Date().toISOString()
                };
            }
            
            console.log('Saving updated printers to localStorage:', localPrinters);
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(localPrinters));
            console.log('Successfully saved printer:', localPrinters[ip]);
            return localPrinters[ip];
        } catch (error) {
            console.error('Error updating printer locally:', error);
            return null;
        }
    },
    
    // Delete a printer locally
    deletePrinter(ip) {
        try {
            const localPrinters = this.getLocalPrinters();
            if (localPrinters[ip]) {
                delete localPrinters[ip];
                localStorage.setItem(this.STORAGE_KEY, JSON.stringify(localPrinters));
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error deleting printer locally:', error);
            return false;
        }
    },
    
    // Merge bridge printers with local customizations
    mergeWithLocalData(bridgePrinters) {
        const localPrinters = this.getLocalPrinters();
        const merged = [];
        
        bridgePrinters.forEach(bridgePrinter => {
            const localPrinter = localPrinters[bridgePrinter.ip];
            if (localPrinter) {
                // Merge bridge data with local customizations
                merged.push({
                    ...bridgePrinter,
                    name: localPrinter.name || bridgePrinter.name,
                    location: localPrinter.location || bridgePrinter.location,
                    lastUpdated: localPrinter.lastUpdated,
                    isCustomized: true
                });
            } else {
                // New printer from bridge
                merged.push({
                    ...bridgePrinter,
                    isCustomized: false
                });
            }
        });
        
        return merged;
    },
    
    // Printer selection storage methods
    STORAGE_KEY_SELECTIONS: 'zWarehouse_printer_selections',
    
    // Get printer selections from local storage
    getPrinterSelections() {
        try {
            const stored = localStorage.getItem(this.STORAGE_KEY_SELECTIONS);
            return stored ? JSON.parse(stored) : {};
        } catch (error) {
            console.error('Error loading printer selections:', error);
            return {};
        }
    },
    
    // Save printer selection locally
    savePrinterSelection(printerType, printer) {
        try {
            const selections = this.getPrinterSelections();
            selections[printerType] = {
                ip: printer.ip,
                name: printer.name || printer.ip,
                location: printer.location || null,
                model: printer.model || null,
                assignedAt: new Date().toISOString(),
                lastUpdated: new Date().toISOString()
            };
            localStorage.setItem(this.STORAGE_KEY_SELECTIONS, JSON.stringify(selections));
            return true;
        } catch (error) {
            console.error('Error saving printer selection:', error);
            return false;
        }
    },
    
    // Remove printer selection locally
    removePrinterSelection(printerType) {
        try {
            const selections = this.getPrinterSelections();
            if (selections[printerType]) {
                delete selections[printerType];
                localStorage.setItem(this.STORAGE_KEY_SELECTIONS, JSON.stringify(selections));
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error removing printer selection:', error);
            return false;
        }
    },
    
    // Clear all printer selections locally
    clearAllPrinterSelections() {
        try {
            localStorage.setItem(this.STORAGE_KEY_SELECTIONS, JSON.stringify({}));
            return true;
        } catch (error) {
            console.error('Error clearing printer selections:', error);
            return false;
        }
    },

    // Update printer selections in localStorage when a printer is updated
    updatePrinterSelectionsForPrinter(updatedPrinter) {
        try {
            const selections = this.getPrinterSelections();
            let updated = false;
            
            Object.keys(selections).forEach(key => {
                if (selections[key] && selections[key].ip === updatedPrinter.ip) {
                    selections[key] = {
                        ...selections[key],
                        name: updatedPrinter.name || selections[key].name,
                        location: updatedPrinter.location || selections[key].location,
                        lastUpdated: new Date().toISOString()
                    };
                    updated = true;
                }
            });
            
            if (updated) {
                localStorage.setItem(this.STORAGE_KEY_SELECTIONS, JSON.stringify(selections));
                console.log('Updated printer selections in localStorage for printer:', updatedPrinter.ip);
            }
            
            return updated;
        } catch (error) {
            console.error('Error updating printer selections for printer:', error);
            return false;
        }
    }
};

export const SettingsScreen = () => {
    const noitfy = useNotify()
    const [settings, setSettings] = useState(
        JSON.parse(localStorage.getItem('zWarehouse')) || {
            environment: 'production',
            printers: {},
            studentLabels: 40,
            qrcode: true
        }
    )
    const [printers, setPrinters] = useState([])
    const [apiPrinters, setApiPrinters] = useState([])
    const [printerSelections, setPrinterSelections] = useState({}) // Store printer selections from API
    const [isScanning, setIsScanning] = useState(false)
    const [selectedPrinterDialog, setSelectedPrinterDialog] = useState(false)
    const [selectedPrinterType, setSelectedPrinterType] = useState('')
    const [apiStatus, setApiStatus] = useState('unknown') // 'unknown', 'connected', 'disconnected', 'checking'
    const [printerDialog, setPrinterDialog] = useState(false)
    const [editingPrinter, setEditingPrinter] = useState(null)
    const [printerForm, setPrinterForm] = useState({
        ip: '',
        name: '',
        location: ''
    })
    const [savingPrinter, setSavingPrinter] = useState(false)
    const [deletingPrinter, setDeletingPrinter] = useState(null)

    const SelectPrinter = (props) => {
        const { name, label } = props
        const selectedPrinter = settings.printers ? settings.printers[name] : null
        const selectedValue = selectedPrinter ? selectedPrinter.name : ''
        
        // Check if the selected value exists in the available printers
        const availablePrinterNames = printers ? printers.map(p => p.name) : []
        const value = availablePrinterNames.includes(selectedValue) ? selectedValue : ''

        return (
            <FormControl variant='standard'>
                <InputLabel>{label}</InputLabel>
                <Select name={name} value={value}
                    onChange={handleChangePrinter} >
                    {printers && printers.map((printer, idx) => (
                        <MenuItem value={printer.name} key={idx}>{printer.name}</MenuItem>
                    ))}
                </Select>
            </FormControl>
        )
    }

    const handleChangePrinter = (e) => {
        const { target: { name, value }} = e
        const printers = { ...settings.printers }
        
        // Find the printer object by name from the available printers
        const selectedPrinter = window.devices && window.devices.printer && 
            window.devices.printer.find(p => p.name === value)
        printers[name] = selectedPrinter || null

        setSettings({ ...settings, printers: printers })
    }

    const handleChangeEnvironment = (e) => {
        const { target: { value }} = e
        setSettings({ ...settings,  environment: value })
        noitfy(`Environment set to ${value}`, { type: 'success' })
    }

    const handleChangeStudentLabel = (e) => {
        const { target: { value }} = e
        console.log("Change Label", value)
        setSettings({ ...settings, studentLabels: value})
        noitfy(`Student Labels set to ${value}`, { type: 'success' })
    }

    const toggleQRCode = (e) => {
        console.log("Toggle QR Code", e)
        const { target: { checked }} = e
        setSettings({ ...settings, qrcode: checked })
        noitfy(`QR Code is ${checked ? 'enabled' : 'disabled'}`, { type: 'success' })
    }

    const scanForPrinters = useCallback(async () => {
        setIsScanning(true)
        try {
            // First try to discover printers (for local bridge)
            try {
                const discoveryResult = await restProvider.discoverPrinters()
                noitfy(`Discovery completed: ${discoveryResult.data.printers?.length || 0} printers found`, { type: 'success' })
            } catch (discoveryError) {
                console.log('Discovery not available, using existing printers:', discoveryError.message)
            }
            
            // Then get the current list of printers from bridge
            const result = await restProvider.getPrintApiPrinters()
            const bridgePrinters = result.data || []
            
            // Merge bridge data with local customizations
            const mergedPrinters = LocalPrinterStorage.mergeWithLocalData(bridgePrinters);
            
            // Update local state with merged data
            setApiPrinters(mergedPrinters)
            
            const printerCount = mergedPrinters.length
            if (printerCount > 0) {
                noitfy(`Found ${printerCount} printers (merged with local customizations)`, { type: 'success' })
            } else {
                noitfy('No printers found', { type: 'info' })
            }
        } catch (error) {
            console.error('Error scanning for printers:', error)
            
            // Provide more specific error messages based on error type
            let errorMessage = 'Failed to scan for printers'
            if (error.message.includes('timeout')) {
                errorMessage = 'Printer scan timed out - check network connection'
            } else if (error.message.includes('Connection refused')) {
                errorMessage = 'Printer bridge service is not running - start the service first'
            } else if (error.message.includes('Network error')) {
                errorMessage = 'Network connection issue - check your internet'
            } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
                errorMessage = 'Cannot connect to printer bridge - check if service is running'
            } else if (error.message.includes('404')) {
                errorMessage = 'Printer discovery endpoint not found'
            } else if (error.message.includes('500')) {
                errorMessage = 'Printer bridge server error during scan'
            }
            
            noitfy(errorMessage, { type: 'warning' })
        } finally {
            setIsScanning(false)
        }
    }, [noitfy])

    // Load printer selections from local storage
    const loadPrinterSelections = useCallback(async () => {
        try {
            const selections = LocalPrinterStorage.getPrinterSelections();
            setPrinterSelections(selections);
            console.log('Loaded printer selections from local storage:', selections);
        } catch (error) {
            console.error('Failed to load printer selections:', error);
            // Don't show error notification as this might be expected if no selections exist
        }
    }, [])

    const refreshPrinters = useCallback(async () => {
        try {
            const result = await restProvider.getPrintApiPrinters()
            const bridgePrinters = result.data || []
            
            // Merge bridge data with local customizations
            const mergedPrinters = LocalPrinterStorage.mergeWithLocalData(bridgePrinters);
            
            // Update local state with merged data
            setApiPrinters(mergedPrinters)
            noitfy(`Refreshed printer list: ${mergedPrinters.length} printers found (merged with local customizations)`, { type: 'success' })
        } catch (error) {
            console.error('Error refreshing printers:', error)
            noitfy('Failed to refresh printer list', { type: 'error' })
        }
    }, [noitfy])

    const checkPrinterApiStatus = useCallback(async () => {
        setApiStatus('checking')
        try {
            await restProvider.getPrintApiStatus()
            setApiStatus('connected')
            noitfy('Printer API is connected', { type: 'success' })
            return 'connected'
        } catch (error) {
            console.error('Error checking printer API status:', error)
            setApiStatus('disconnected')
            
            // Provide more specific error messages based on error type
            let errorMessage = 'Printer API is not available'
            if (error.message.includes('timeout')) {
                errorMessage = 'Printer API connection timed out'
            } else if (error.message.includes('Connection refused')) {
                errorMessage = 'Printer API service is not running or unavailable'
            } else if (error.message.includes('Network error')) {
                errorMessage = 'Network connection issue - check your internet'
            } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
                errorMessage = 'Printer API connection refused or network error'
            } else if (error.message.includes('404')) {
                errorMessage = 'Printer API endpoint not found'
            } else if (error.message.includes('500')) {
                errorMessage = 'Printer API server error'
            }
            
            noitfy(errorMessage, { type: 'warning' })
            return 'disconnected'
        }
    }, [noitfy])

    const openPrinterSelection = async (printerType) => {
        setSelectedPrinterType(printerType)
        setSelectedPrinterDialog(true)
        
        // Use the current printer list from API
    }

    const selectPrinter = async (printer) => {
        try {
            // Set printer selection locally
            if (LocalPrinterStorage.savePrinterSelection(selectedPrinterType, printer)) {
                // Update local state
                setPrinterSelections(prev => ({
                    ...prev,
                    [selectedPrinterType]: {
                        ip: printer.ip,
                        name: printer.name || printer.ip,
                        location: printer.location || null,
                        model: printer.model || null,
                        assignedAt: new Date().toISOString(),
                        lastUpdated: new Date().toISOString()
                    }
                }));
                
                setSelectedPrinterDialog(false);
                noitfy(`${printer.name || printer.ip} selected for ${selectedPrinterType} (saved locally)`, { type: 'success' });
            } else {
                noitfy('Failed to save printer selection locally', { type: 'error' });
            }
        } catch (error) {
            console.error('Failed to set printer selection:', error);
            noitfy(`Failed to set printer selection: ${error.message}`, { type: 'error' });
        }
    }

    const getSelectedPrinter = (printerType) => {
        const selected = printerSelections[printerType] || null;
        console.log(`getSelectedPrinter(${printerType}):`, selected);
        return selected;
    }

    const deselectPrinter = async (printerType) => {
        try {
            // Remove printer selection locally
            if (LocalPrinterStorage.removePrinterSelection(printerType)) {
                // Update local state
                setPrinterSelections(prev => {
                    const newSelections = { ...prev };
                    delete newSelections[printerType];
                    return newSelections;
                });
                
                noitfy(`${printerType} printer deselected (removed locally)`, { type: 'info' });
            } else {
                noitfy('Failed to remove printer selection locally', { type: 'error' });
            }
        } catch (error) {
            console.error('Failed to remove printer selection:', error);
            noitfy(`Failed to remove printer selection: ${error.message}`, { type: 'error' });
        }
    }

    const clearAllPrinters = async () => {
        try {
            // Clear all printer selections locally
            if (LocalPrinterStorage.clearAllPrinterSelections()) {
                // Update local state
                setPrinterSelections({});
                noitfy('All printer selections cleared (removed locally)', { type: 'info' });
            } else {
                noitfy('Failed to clear printer selections locally', { type: 'error' });
            }
        } catch (error) {
            console.error('Failed to clear printer selections:', error);
            noitfy(`Failed to clear printer selections: ${error.message}`, { type: 'error' });
        }
    }

    const openPrinterDialog = (printer = null) => {
        if (printer) {
            setEditingPrinter(printer)
            setPrinterForm({
                ip: printer.ip,
                name: printer.name || '',
                location: printer.location || ''
            })
        } else {
            setEditingPrinter(null)
            setPrinterForm({
                ip: '',
                name: '',
                location: ''
            })
        }
        setPrinterDialog(true)
    }

    const closePrinterDialog = () => {
        setPrinterDialog(false)
        setEditingPrinter(null)
        setPrinterForm({
            ip: '',
            name: '',
            location: ''
        })
    }

    const handlePrinterFormChange = (field, value) => {
        setPrinterForm(prev => ({
            ...prev,
            [field]: value
        }))
    }

    const savePrinter = async () => {
        if (!printerForm.ip.trim()) {
            noitfy('IP address is required', { type: 'error' })
            return
        }

        setSavingPrinter(true)
        try {
            if (editingPrinter) {
                console.log('Updating printer locally:', editingPrinter.ip, printerForm);
                console.log('editingPrinter object:', editingPrinter);
                
                // Update printer locally
                const updatedPrinter = LocalPrinterStorage.updatePrinter(editingPrinter.ip, {
                    name: printerForm.name.trim() || null,
                    location: printerForm.location.trim() || null
                });
                
                console.log('LocalPrinterStorage.updatePrinter result:', updatedPrinter);
                
                if (updatedPrinter) {
                    // Update local state
                    setApiPrinters(prev => prev.map(p => 
                        p.ip === editingPrinter.ip ? updatedPrinter : p
                    ));
                    
                    // Update any selected printers that reference this printer in localStorage
                    LocalPrinterStorage.updatePrinterSelectionsForPrinter(updatedPrinter);
                    
                    // Refresh printer selections from localStorage to ensure they're in sync
                    const refreshedSelections = LocalPrinterStorage.getPrinterSelections();
                    setPrinterSelections(refreshedSelections);
                    
                    noitfy('Printer updated successfully (saved locally)', { type: 'success' });
                    
                    // Close the dialog and reset form
                    setPrinterDialog(false);
                    setEditingPrinter(null);
                    setPrinterForm({
                        ip: '',
                        name: '',
                        location: ''
                    });
                    
                } else {
                    noitfy('Failed to update printer locally', { type: 'error' });
                }
            } else {
                console.log('Adding new printer locally:', printerForm);
                
                // Create new printer object
                const newPrinter = {
                    ip: printerForm.ip.trim(),
                    name: printerForm.name.trim() || null,
                    location: printerForm.location.trim() || null,
                    port: 9100,
                    status: 'unknown',
                    model: null,
                    discoveredAt: new Date().toISOString(),
                    discoveryMethod: 'manual'
                };
                
                // Save locally
                if (LocalPrinterStorage.savePrinter(newPrinter)) {
                    // Add to local state
                    setApiPrinters(prev => [...prev, newPrinter]);
                    
                    noitfy('Printer added successfully (saved locally)', { type: 'success' });
                    
                    // Close the dialog and reset form
                    setPrinterDialog(false);
                    setEditingPrinter(null);
                    setPrinterForm({
                        ip: '',
                        name: '',
                        location: ''
                    });
                    
                } else {
                    noitfy('Failed to add printer locally', { type: 'error' });
                }
            }
        } catch (error) {
            console.error('Error saving printer:', error)
            noitfy(error.message || 'Failed to save printer', { type: 'error' })
        } finally {
            setSavingPrinter(false)
        }
    }

    const deletePrinter = async (printer) => {
        setDeletingPrinter(printer.ip)
        try {
            // Delete locally
            if (LocalPrinterStorage.deletePrinter(printer.ip)) {
                // Remove from local state
                const updatedPrinters = apiPrinters.filter(p => p.ip !== printer.ip)
                setApiPrinters(updatedPrinters)
                
                // Also remove this printer from any selected printers
                setPrinterSelections(prev => {
                    const updatedSelections = { ...prev };
                    Object.keys(updatedSelections).forEach(key => {
                        if (updatedSelections[key] && updatedSelections[key].ip === printer.ip) {
                            delete updatedSelections[key];
                        }
                    });
                    return updatedSelections;
                });
                
                noitfy('Printer deleted successfully (removed locally)', { type: 'success' })
            } else {
                noitfy('Failed to delete printer locally', { type: 'error' })
            }
        } catch (error) {
            console.error('Error deleting printer:', error)
            noitfy(error.message || 'Failed to delete printer', { type: 'error' })
        } finally {
            setDeletingPrinter(null)
        }
    }

    useEffect(() => {
        if (window.devices) {
            const {printer } = window.devices
            setPrinters(printer)
        }
    }, [])

    useEffect(() => {
        localStorage.setItem('zWarehouse', JSON.stringify(settings))
    }, [settings])

    useEffect(() => {
        const initializeSettings = async () => {
            const status = await checkPrinterApiStatus()
            
            // Load any existing local printers
            const localPrinters = LocalPrinterStorage.getLocalPrinters();
            if (Object.keys(localPrinters).length > 0) {
                console.log('Loading existing local printers:', Object.keys(localPrinters).length);
                const localPrinterArray = Object.values(localPrinters);
                setApiPrinters(localPrinterArray);
            }
            
            // Load printer selections from local storage
            await loadPrinterSelections()
        }
        initializeSettings()
    }, [checkPrinterApiStatus, loadPrinterSelections])

    return (
        <Container sx={{ p: 2 }}>

            <Stack direction='row' spacing={2} alignItems='center'>
                <SettingsInputComponentTwoToneIcon color='primary' sx={{
                    fontSize: 40
                }} />
                <Typography variant='h4' color='primary'>Settings</Typography>
            </Stack>
            
            <Grid container spacing={4} mt={2}>
                <Grid item sm={5}>
                    <Card>
                        <CardContent>
                            <Typography fontSize={12} color='gray'>Legacy Printers</Typography>
                            <Stack spacing={2}>

                                <SelectPrinter name='box' label='Box Label Printer' />
                                <SelectPrinter name='bag' label='Bag Label Printer' />
                                <SelectPrinter name='student' label='Student Labels Printer' />
                            </Stack>
                        </CardContent>
                    </Card>

                </Grid>

                <Grid item sm={4}>
                    <Stack sx={{ mt: 2 }} spacing={4}>
                        <FormControl variant="standard">
                            <InputLabel>Environment</InputLabel>
                            <Select
                                value={settings.environment}
                                onChange={handleChangeEnvironment}>
                                <MenuItem value='dev'>Dev</MenuItem>
                                <MenuItem value='staging'>Staging</MenuItem>
                                <MenuItem value='production'>Production</MenuItem>
                            </Select>
                        </FormControl>

                        <InputLabel>Number of Student Labels</InputLabel>
                        <FormControl>
                            <Input 
                                type='number' 
                                value={settings.studentLabels} 
                                size="medium"
                                onChange={handleChangeStudentLabel}
                                placeholder="40"
                                inputProps={{ min: 1, max: 100 }}
                            />
                        </FormControl>

                        <Stack direction='row' spacing={2} alignItems='center'>
                            <Switch onChange={toggleQRCode} checked={settings.qrcode} />
                            <InputLabel>QR Code</InputLabel>
                        </Stack>

                    </Stack>
                </Grid>
            </Grid>

            <Card sx={{ mt: 2 }}>
                <CardContent sx={{ p: 3 }}>
                    <Stack direction='row' spacing={2} alignItems='center' mb={2}>
                        <Typography fontSize={12} color='gray'>Printer API</Typography>
                        <Stack direction='row' spacing={1} alignItems='center'>
                            <Chip 
                                label={
                                    apiStatus === 'connected' ? 'Connected' :
                                    apiStatus === 'disconnected' ? 'Disconnected' :
                                    apiStatus === 'checking' ? 'Checking...' : 'Unknown'
                                }
                                size="small"
                                color={
                                    apiStatus === 'connected' ? 'success' :
                                    apiStatus === 'disconnected' ? 'error' :
                                    apiStatus === 'checking' ? 'warning' : 'default'
                                }
                                icon={apiStatus === 'checking' ? <CircularProgress size={16} /> : undefined}
                            />
                        </Stack>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={isScanning ? <CircularProgress size={16} /> : <RefreshIcon />}
                            onClick={scanForPrinters}
                            disabled={isScanning || apiStatus === 'disconnected'}
                        >
                            {isScanning ? 'Scanning...' : 'Scan for Printers'}
                        </Button>
                        <Button
                            variant="text"
                            size="small"
                            onClick={checkPrinterApiStatus}
                            disabled={apiStatus === 'checking'}
                        >
                            Check Status
                        </Button>
                    </Stack>

                    <Stack spacing={2} mb={3}>
                        <Stack direction='row' justifyContent='space-between' alignItems='center'>
                            <Typography variant="subtitle2" color="text.secondary">Selected API Printers</Typography>
                            {Object.keys(printerSelections || {}).length > 0 && (
                                <Button
                                    size="small"
                                    variant="text"
                                    color="error"
                                    onClick={clearAllPrinters}
                                    startIcon={<ClearIcon fontSize="small" />}
                                >
                                    Clear All
                                </Button>
                            )}
                        </Stack>
                        <Grid container spacing={1.5}>
                            <Grid item xs={12} sm={4}>
                                <Card variant="outlined" sx={{ p: 1.5 }}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>Box Label Printer</Typography>
                                    {getSelectedPrinter('box') ? (
                                        <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
                                            <Stack direction='row' spacing={1} alignItems='center' sx={{ minWidth: 0, flex: 1 }}>
                                                <CheckIcon color="success" fontSize="small" />
                                                <Stack sx={{ minWidth: 0 }}>
                                                    <Typography variant="body2" noWrap>
                                                        {getSelectedPrinter('box').name || getSelectedPrinter('box').ip}
                                                    </Typography>
                                                    {getSelectedPrinter('box').location && (
                                                        <Typography variant="caption" color="text.secondary" noWrap>
                                                            {getSelectedPrinter('box').location}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Stack>
                                            <Button
                                                size="small"
                                                variant="text"
                                                color="error"
                                                onClick={() => deselectPrinter('box')}
                                                sx={{ minWidth: 'auto', px: 1 }}
                                            >
                                                <ClearIcon fontSize="small" />
                                            </Button>
                                        </Stack>
                                    ) : (
                                        <Button 
                                            size="small" 
                                            variant="outlined" 
                                            onClick={() => openPrinterSelection('box')} 
                                            disabled
                                        >
                                            Select Printer
                                        </Button>
                                    )}
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Card variant="outlined" sx={{ p: 1.5 }}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>Bag Label Printer</Typography>
                                    {getSelectedPrinter('bag') ? (
                                        <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
                                            <Stack direction='row' spacing={1} alignItems='center' sx={{ minWidth: 0, flex: 1 }}>
                                                <CheckIcon color="success" fontSize="small" />
                                                <Stack sx={{ minWidth: 0 }}>
                                                    <Typography variant="body2" noWrap>
                                                        {getSelectedPrinter('bag').name || getSelectedPrinter('bag').ip}
                                                    </Typography>
                                                    {getSelectedPrinter('bag').location && (
                                                        <Typography variant="caption" color="text.secondary" noWrap>
                                                            {getSelectedPrinter('bag').location}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Stack>
                                            <Button
                                                size="small"
                                                variant="text"
                                                color="error"
                                                onClick={() => deselectPrinter('bag')}
                                                sx={{ minWidth: 'auto', px: 1 }}
                                            >
                                                <ClearIcon fontSize="small" />
                                            </Button>
                                        </Stack>
                                    ) : (
                                        <Button 
                                            size="small" 
                                            variant="outlined" 
                                            onClick={() => openPrinterSelection('bag')} 
                                            disabled
                                        >
                                            Select Printer
                                        </Button>
                                    )}
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Card variant="outlined" sx={{ p: 1.5 }}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>Student Labels Printer</Typography>
                                    {getSelectedPrinter('student') ? (
                                        <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
                                            <Stack direction='row' spacing={1} alignItems='center' sx={{ minWidth: 0, flex: 1 }}>
                                                <CheckIcon color="success" fontSize="small" />
                                                <Stack sx={{ minWidth: 0 }}>
                                                    <Typography variant="body2" noWrap>
                                                        {getSelectedPrinter('student').name || getSelectedPrinter('student').ip}
                                                    </Typography>
                                                    {getSelectedPrinter('student').location && (
                                                        <Typography variant="caption" color="text.secondary" noWrap>
                                                            {getSelectedPrinter('student').location}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Stack>
                                            <Button
                                                size="small"
                                                variant="text"
                                                color="error"
                                                onClick={() => deselectPrinter('student')}
                                                sx={{ minWidth: 'auto', px: 1 }}
                                            >
                                                <ClearIcon fontSize="small" />
                                            </Button>
                                        </Stack>
                                    ) : (
                                        <Button 
                                            size="small" 
                                            variant="outlined" 
                                            onClick={() => openPrinterSelection('student')}
                                            disabled={apiStatus === 'disconnected'}
                                        >
                                            Select Printer
                                        </Button>
                                    )}
                                </Card>
                            </Grid>
                        </Grid>
                    </Stack>

                    <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
                        <Stack direction='row' spacing={2} alignItems='center'>
                            <Typography variant="subtitle2" color="text.secondary">Available Printers</Typography>
                            <Chip
                                label={apiStatus === 'connected' ? 'API Connected' : apiStatus === 'checking' ? 'Checking...' : 'API Disconnected'}
                                size="small"
                                color={apiStatus === 'connected' ? 'success' : apiStatus === 'checking' ? 'warning' : 'error'}
                            />
                        </Stack>
                        <Stack direction='row' spacing={1}>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={refreshPrinters}
                                disabled={isScanning}
                                title="Refresh printer list from API"
                            >
                                Refresh
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() => openPrinterDialog()}
                            >
                                Add Printer
                            </Button>
                        </Stack>
                    </Stack>
                    {apiStatus === 'checking' && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <AlertTitle>Checking Printer API</AlertTitle>
                            Verifying connection to the printer service...
                        </Alert>
                    )}
                    {apiStatus === 'disconnected' && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <AlertTitle>Printer API Disconnected</AlertTitle>
                            The printer API service is not available. Please ensure the printer service is running and accessible at {process.env.REACT_APP_PRINTER_API_URL || 'the configured URL'}.
                        </Alert>
                    )}
                    {apiStatus === 'connected' && apiPrinters.length === 0 && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <AlertTitle>No Printers Found</AlertTitle>
                            The printer API is connected but no printers are available. Click "Scan for Printers" to discover available printers or "Add Printer" to manually add one.
                        </Alert>
                    )}
                    {apiStatus === 'connected' && apiPrinters.length > 0 && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <AlertTitle>Printer Management</AlertTitle>
                            Edit printer names and locations, then click Save. Changes are saved locally and will persist in your browser. Use the Refresh button to get latest printer status from the bridge. Each user manages their own printer names and locations.
                        </Alert>
                    )}
                    {apiPrinters.length > 0 ? (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>IP</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Model</TableCell>
                                    <TableCell>Location</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {apiPrinters.map((printer, index) => (
                                    <TableRow key={`main-${printer.ip}-${printer.name}-${printer.location}`}>
                                        <TableCell>{printer.ip}</TableCell>
                                        <TableCell>{printer.name || '-'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={printer.status || 'unknown'}
                                                size="small"
                                                color={printer.status === 'online' ? 'success' : 'error'}
                                            />
                                        </TableCell>
                                        <TableCell>{printer.model || '-'}</TableCell>
                                        <TableCell>{printer.location || '-'}</TableCell>
                                        <TableCell>
                                            <Stack direction='row' spacing={1}>
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => openPrinterDialog(printer)}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => deletePrinter(printer)}
                                                    disabled={deletingPrinter === printer.ip}
                                                >
                                                    {deletingPrinter === printer.ip ? (
                                                        <CircularProgress size={16} />
                                                    ) : (
                                                        <DeleteIcon fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                            {apiStatus === 'connected' 
                                ? 'No printers available. Use the buttons above to scan for printers or add one manually.'
                                : 'Printer API is not connected. Please check the connection and try again.'
                            }
                        </Typography>
                    )}
                </CardContent>
            </Card>

            {/* Printer Selection Dialog */}
            <Dialog open={selectedPrinterDialog} onClose={() => setSelectedPrinterDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Select {selectedPrinterType} Printer</DialogTitle>
                <DialogContent>
                    {apiPrinters.length > 0 ? (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>IP</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Model</TableCell>
                                    <TableCell>Location</TableCell>
                                    <TableCell>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {apiPrinters.map((printer, index) => (
                                    <TableRow key={`selection-${printer.ip}-${printer.name}-${printer.location}`}>
                                        <TableCell>{printer.ip}</TableCell>
                                        <TableCell>{printer.name || '-'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={printer.status || 'unknown'}
                                                size="small"
                                                color={printer.status === 'online' ? 'success' : 'error'}
                                            />
                                        </TableCell>
                                        <TableCell>{printer.model || '-'}</TableCell>
                                        <TableCell>{printer.location || '-'}</TableCell>
                                        <TableCell>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={() => selectPrinter(printer)}
                                            >
                                                Select
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                            No printers available. Please scan for printers first.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => setSelectedPrinterDialog(false)}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Printer Dialog */}
            <Dialog open={printerDialog} onClose={closePrinterDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingPrinter ? 'Edit Printer' : 'Add New Printer'}
                </DialogTitle>
                <DialogContent>
                    {editingPrinter ? (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <AlertTitle>Editing Printer</AlertTitle>
                            Update the name and location for this printer. Changes are saved locally and will persist in your browser.
                        </Alert>
                    ) : (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <AlertTitle>Adding New Printer</AlertTitle>
                            Enter the IP address of the printer. The system will create a local entry that you can customize.
                        </Alert>
                    )}
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField
                            label="IP Address"
                            value={printerForm.ip}
                            onChange={(e) => handlePrinterFormChange('ip', e.target.value)}
                            fullWidth
                            required
                            placeholder="192.168.1.100"
                        />
                        <TextField
                            label="Name (Optional)"
                            value={printerForm.name}
                            onChange={(e) => handlePrinterFormChange('name', e.target.value)}
                            fullWidth
                            placeholder="Printer Name"
                        />
                        <TextField
                            label="Location (Optional)"
                            value={printerForm.location}
                            onChange={(e) => handlePrinterFormChange('location', e.target.value)}
                            fullWidth
                            placeholder="Office, Warehouse, etc."
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={closePrinterDialog}>
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={savePrinter}
                        disabled={!printerForm.ip.trim() || savingPrinter}
                        startIcon={savingPrinter ? <CircularProgress size={16} /> : undefined}
                    >
                        {savingPrinter ? 'Saving...' : (editingPrinter ? 'Update' : 'Add')} Printer
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}